// React Query hooks for /staff/users/* (feature F-A). Mirrors useZones.ts.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MessageResponse,
  User,
  UserCreate,
  UserUpdate,
  createUser,
  deleteUser,
  disableUser,
  enableUser,
  getUser,
  listUsers,
  resetUserPassword,
  updateUser,
} from "../users";

export const userKeys = {
  all: ["users"] as const,
  list: (includeDeleted: boolean) =>
    [...userKeys.all, "list", { includeDeleted }] as const,
  detail: (id: string) => [...userKeys.all, "detail", id] as const,
};

export function useUsers(includeDeleted = false) {
  return useQuery<User[]>({
    queryKey: userKeys.list(includeDeleted),
    queryFn: () => listUsers({ includeDeleted }),
  });
}

export function useUser(userId: string | undefined) {
  return useQuery<User>({
    queryKey: userKeys.detail(userId ?? ""),
    queryFn: () => getUser(userId as string),
    enabled: Boolean(userId),
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: userKeys.all });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<User, Error, UserCreate>({
    mutationFn: createUser,
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<User, Error, { userId: string; payload: UserUpdate }>({
    mutationFn: ({ userId, payload }) => updateUser(userId, payload),
    onSuccess: (updated) => {
      invalidateAll(qc);
      qc.setQueryData(userKeys.detail(updated.id), updated);
    },
  });
}

export function useEnableUser() {
  const qc = useQueryClient();
  return useMutation<User, Error, string>({
    mutationFn: enableUser,
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDisableUser() {
  const qc = useQueryClient();
  return useMutation<User, Error, string>({
    mutationFn: disableUser,
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<MessageResponse, Error, string>({
    mutationFn: deleteUser,
    onSuccess: () => invalidateAll(qc),
  });
}

export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation<
    MessageResponse,
    Error,
    { userId: string; password: string }
  >({
    mutationFn: ({ userId, password }) => resetUserPassword(userId, password),
    onSuccess: () => invalidateAll(qc),
  });
}
